/**
 * AdvisorSelect — B.4 (2026-05-08)
 *
 * Replaces the free-form "advisor name" text input in Step 1 with a
 * curated dropdown of the 5 active sales advisors. The selection
 * carries three pieces of data forward into the session:
 *
 *   • advisorId         — stable slug ("mondher")
 *   • advisorName       — localized display name (passed as `value` label)
 *   • advisorRespondId  — numeric id used by the Worker to set the
 *                         conversation assignee in respond.io after
 *                         the WhatsApp template lands
 *
 * The Arabic name is canonical for the WhatsApp Template (which is
 * AR-only). Caller decides which name to show on screen.
 */

import advisorsData from '@data/advisors.json';
import type { Advisor, Language } from '@lib/types';

interface AdvisorSelectProps {
  id?: string;
  value: string;                  // selected advisor.id, '' = none
  onChange: (advisor: Advisor | null) => void;
  language: Language;
  placeholder: string;
  invalid?: boolean;
  required?: boolean;
}

const ALL_ADVISORS = (advisorsData as { advisors: Advisor[] }).advisors;

export default function AdvisorSelect({
  id,
  value,
  onChange,
  language,
  placeholder,
  invalid,
  required,
}: AdvisorSelectProps) {
  const active = ALL_ADVISORS.filter((a) => a.active);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value;
    if (!next) {
      onChange(null);
      return;
    }
    const advisor = active.find((a) => a.id === next) ?? null;
    onChange(advisor);
  };

  return (
    <select
      id={id}
      value={value}
      onChange={handleChange}
      required={required}
      aria-invalid={invalid || undefined}
      className={[
        'w-full px-3 py-2.5 rounded-lg border bg-white',
        'text-navy',
        'transition-colors duration-150',
        'focus:outline-none focus:ring-2 focus:ring-gold/40',
        invalid
          ? 'border-sold focus:border-sold'
          : 'border-navy-100 focus:border-gold',
      ].join(' ')}
    >
      <option value="">{placeholder}</option>
      {active.map((a) => (
        <option key={a.id} value={a.id}>
          {a.name[language]}
        </option>
      ))}
    </select>
  );
}
